import { AppContext } from "App";

import { IUserSettingsClient } from "@multiweb/clients/userSettings";
import { IOpenedFormHistoryInfo } from "@multiweb/types/contracts";
import { Event } from "../../../../Global/Base/Events";
import { CancellationToken } from "../../../../Global/Base/Promises/CancellationTokenSource";
import { RequestContextWithoutTransactionContextId } from "../../../../OW.Remoting/Remoting/RequestContextWithoutTransactionContextId";
import { IWindowable } from "../UIManagerInterfaces";
import { IForm } from "../XGControls/IForm";
import { IOpenedFormsHistoryManager } from "./IOpenedFormsHistoryManager";

/**
 * Umožňuje spravovat data historie otevřených formulářů.
 * V současné implementaci nejde ukládat formulář, který nebyl otevřen z položky navigačního menu (neměl by vazbu na menuItem).
 */
export class OpenedFormsHistoryManager implements IOpenedFormsHistoryManager {
    private readonly _formHistoryUpdated: Event<OpenedFormsHistoryManager, readonly Readonly<IOpenedFormHistoryInfo>[]> = new Event<OpenedFormsHistoryManager, readonly Readonly<IOpenedFormHistoryInfo>[]>();
    private readonly _client: IUserSettingsClient;
    private readonly _maxHistoryItems: number = 5;

    private __historyInfo: readonly IOpenedFormHistoryInfo[] = [];
    private _getOpenedFormsHistoryPromise: Promise<void> | null;

    /**
     * Pole pro položky historie, které jsou určené ke smazání z menu sekce historie.
     */
    private _historyInfosToBeRemoved: IOpenedFormHistoryInfo[] = [];

    public get formHistoryUpdated(): Event<OpenedFormsHistoryManager, readonly Readonly<IOpenedFormHistoryInfo>[]> {
        return this._formHistoryUpdated;
    }

    private get _historyInfo(): readonly IOpenedFormHistoryInfo[] {
        return this.__historyInfo;
    }

    private set _historyInfo(infoArray: readonly IOpenedFormHistoryInfo[]) {
        this.__historyInfo = this._getCroppedHistoryInfo(infoArray);
    }

    constructor(client: IUserSettingsClient, maxHistoryItems: number) {
        this._client = client;
        this._maxHistoryItems = maxHistoryItems;
    }

    /**
     * Přidání a mazání formuláře do/z historie. Historie je automaticky ihned uložena.
     * Tato metoda je volána v případech, kdy je otevřen nebo zavřen formulář.
     * Pokud je formulář nově otevřen, je přidán do historie (`this._historyInfo`) a po zavření se zobrazí v sekci menu historie.
     * Pokud je formulář zavřen a v průběhu otevření došlo ke smazání profilu (přidání historie do pole `_getOpenedFormsHistoryPromise`), historie se po zavření promaže.
     *
     * @param form Formulář k uložení do historie
     * @param menuItemId Id položky menu, ke které formulář patří
     */
    public async updateOpenedFormsHistory(form: IWindowable, menuItemId: number): Promise<void> {
        if (this._maxHistoryItems === 0) {
            if (this._historyInfo.length > 0) {
                this._historyInfo = [];
                this.formHistoryUpdated.trigger(this, this._historyInfo);
                await this._client.saveOpenedFormsHistory(this._historyInfo, this._getRequestContext());
            }
            return;
        }

        if (form instanceof IForm) {
            // formulář se při otevření smaže z historie a poté je tam vrácen na poslední místo jako historicky poslední otevřený formulář
            this._historyInfo = this._remove(this._historyInfo, form, menuItemId);

            const historyInfo = { formCode: form.formName, menuItemId: menuItemId, profileId: form.initialFormProfileId ? +form.initialFormProfileId : undefined };

            if (this._shouldInsertToHistoryInfo(historyInfo)) {
                // pokud není určen ke smazání, přidá se na poslední místo jako historicky poslední otevřený formulář
                this._insertToHistoryInfo(historyInfo);
            }

            if (this._historyInfosToBeRemoved.length > 0) {
                // Pokud pole obsahuje nějaké položky, tak jsou určeny ke smazání ze sekce historie.
                this._removeHistoryInfos();
            }

            this.formHistoryUpdated.trigger(this, this._historyInfo);
            await this._client.saveOpenedFormsHistory(this._historyInfo, this._getRequestContext());
        }
    }

    public async getOpenedFormsHistory(): Promise<readonly IOpenedFormHistoryInfo[]> {
        if (!this._getOpenedFormsHistoryPromise)
            this._getOpenedFormsHistoryPromise = (async () => {
                this._historyInfo = await this._client.getOpenedFormsHistory(this._getRequestContext());
            })();

        await this._getOpenedFormsHistoryPromise;

        return this._historyInfo;
    }

    /**
     * Označí položku nebo profil patřící k formuláři `form`, který bude po provolání metody {@link updateOpenedFormsHistory} smazán.
     * Dříve se mazalo z historie přímo po kliku na mazanou položku, teď se daná položka nejprve označí (vloží do pole _historyInfosToBeRemoved)
     * a teprve pak je v metodě `updateOpenedFormsHistory` mazání zprocesováno viz {@link updateOpenedFormsHistory}.
     *
     * Pokud je definováno `menuItemId`, označí ke smazání položku s tímto id.
     * Pokud je definováno `profileId`, označí ke smazání profil s tímto id.
     * @param form Formulář jehož položky budeme z historie odstraňovat.
     * @param menuItemId Id položky menu.
     * @param profileId Id profilu.
     * @return Promise s hodnotou `true` pokud se odstranila nějaká položka, jinak s hodnotou `false`.
     */
    public markOpenedFormsHistoryItemToBeRemovedOnFormClose(form: IForm, menuItemId?: number, profileId?: number): boolean {
        let recentlyRemovedHistoryInfos: IOpenedFormHistoryInfo[] = [];

        if (menuItemId) {
            recentlyRemovedHistoryInfos = this._historyInfo.filter(item => item.formCode === form.formName && item.menuItemId === menuItemId);
        }
        if (profileId) {
            let recentlyRemovedHistoryInfo: IOpenedFormHistoryInfo;
            if (recentlyRemovedHistoryInfos.length > 0) {
                // jestliže historie obsahuje položky s profilem, vyhledá na základě profileId unikátní položku
                recentlyRemovedHistoryInfo = recentlyRemovedHistoryInfos.find(item => item.formCode === form.formName && item.profileId === profileId);
            } else {
                // pokud není menuItemId zadáno a tím pádem dosud nebyly vyhledány položky ke smazání, zkusí se vyhledat na základě profileId
                recentlyRemovedHistoryInfo = this._historyInfo.find(item => item.formCode === form.formName && item.profileId === profileId);
            }

            if (recentlyRemovedHistoryInfo) {
                this._historyInfosToBeRemoved.push(recentlyRemovedHistoryInfo);
                return true;
            }
        }

        if (recentlyRemovedHistoryInfos.length > 0) {
            this._historyInfosToBeRemoved = recentlyRemovedHistoryInfos;
            return true;
        }
        return false;
    }

    /**
     * Smaže z historie aktuálně otevřený formulář.
     * @param history formulář otevřený v minulosti
     * @param form aktuální formulář
     * @param menuItemId if položky otevírající aktuální formulář
     * @private
     */
    private _remove(history: readonly IOpenedFormHistoryInfo[], form: IForm, menuItemId?: number): readonly IOpenedFormHistoryInfo[] {
        const formProfileId = form.initialFormProfileId ? +form.initialFormProfileId : undefined;
        return history.filter((item) => item.formCode !== form.formName || item.menuItemId !== menuItemId || item.profileId !== formProfileId);
    }

    /**
     * Vrací pole ořezané na nakonfigurovaný maximální počet položek
     */
    private _getCroppedHistoryInfo(infoArray: readonly IOpenedFormHistoryInfo[]): readonly IOpenedFormHistoryInfo[] {
        return (infoArray.length > this._maxHistoryItems) ? infoArray.slice(infoArray.length - this._maxHistoryItems) : infoArray;
    }

    /**
     * Vloží položku historie na poslední místo pole property `_historyInfo` a tu ořeže na nakonfigurovaný maximální počet položek
     * @param historyInfoItem Položka historie ke vložení
     */
    private _insertToHistoryInfo(historyInfoItem: IOpenedFormHistoryInfo): void {
        this._historyInfo = [...this._historyInfo, historyInfoItem];
    }

    /**
     * Request kontext pro práci v menu aplikace.
     * Menu nepracuje s transakčním kontextem. Stačí proto {@link RequestContextWithoutTransactionContextId}.
     */
    private _getRequestContext(cancellationToken: CancellationToken = CancellationToken.none): RequestContextWithoutTransactionContextId {
        return {
            roles: AppContext.instance.activeUserRolesIds,
            cancellationToken,
        };
    }

    /**
     * Položky označené ke smazání se vymažou ze sekce historie.
     * @private
     */
    private _removeHistoryInfos(): void {
        this._historyInfo = this._historyInfo.filter(info => this._shouldInsertToHistoryInfo(info));
        // Po smazání pole vyčistíme.
        this._historyInfosToBeRemoved = [];
    }

    /**
     * Metoda určuje, jestli se má položka zobrazovat v sekci historie. Pokud je položka označena ke smazání, nebude se vkládat do sekce a naopak.
     * @param historyInfo
     * @private
     */
    private _shouldInsertToHistoryInfo(historyInfo: IOpenedFormHistoryInfo): boolean {
        return !this._historyInfosToBeRemoved.some(info => this._historyInfosEqual(historyInfo, info));
    }

    private _historyInfosEqual(historyInfo1: IOpenedFormHistoryInfo, historyInfo2: IOpenedFormHistoryInfo): boolean {
        return historyInfo1.profileId === historyInfo2.profileId
            && historyInfo1.formCode === historyInfo2.formCode
            && historyInfo1.menuItemId === historyInfo2.menuItemId;
    }
}
